import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Filter,
  X,
  Plus
} from 'lucide-react';
import { movimientoService, MovimientoFilters, PaginatedResponse } from '../../services/movimientoService';
import { OwnerContext } from '../../types/actividad';
import { GastoModal } from '../GastoModal';
import { IngresoJAModal } from './IngresoJAModal';
import { Select } from '../ui/Select';
import { formatDate } from '../../utils/formatters';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { MovimientoCampanaModal } from '../MovimientoCampanaModal';
import { useAuth } from '../../context/AuthContext';
import { puedeOperarDinero } from '../../utils/permissions';
import { useRegularizacion } from '../../context/RegularizacionContext';
import { ComprobantesPendientesPanel } from '../comprobantes/ComprobantesPendientesPanel';
import { ComprobantesDeMovimiento } from '../comprobantes/ComprobantesDeMovimiento';
import { ReembolsoModal } from '../comprobantes/ReembolsoModal';
import { MontoCorregido } from '../comprobantes/MontoCorregido';
import { AdjuntarComprobanteModal, type ItemParaAdjuntar } from '../comprobantes/AdjuntarComprobanteModal';
import type { Movimiento } from '../../lib/supabase';
import type { EntidadDinero, Respaldo } from '../../types/comprobante';

interface MovimientosJAViewProps {
  readonly ownerContext: OwnerContext;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);

interface MovOrigen {
  readonly origen_tipo?: string;
  readonly categoria_egreso?: string;
  readonly categoria_aporte?: string;
  readonly modo_pago_cuota_iglesia?: string | null;
}

const CLS_INGRESO = 'text-emerald-600';
const CLS_EGRESO = 'text-red-600';
const CLS_ANULADO = 'text-slate-400';

const CATEGORIA_EGRESO_LABELS: Record<string, string> = {
  general: 'General',
  liturgico: 'Litúrgico',
  misional: 'Misional',
};

function esCuotaPA(mov: MovOrigen): boolean {
  return mov.categoria_aporte === 'cuota_iglesia' && mov.modo_pago_cuota_iglesia != null;
}

function getOrigenBadgeClass(mov: MovOrigen): string {
  if (mov.categoria_aporte === 'cuota_iglesia') {
    return 'bg-slate-200 text-slate-600';
  }
  switch (mov.origen_tipo) {
    case 'miembro': return 'bg-blue-100 text-blue-700';
    case 'externo': return 'bg-violet-100 text-violet-700';
    case 'lumisial': return 'bg-cyan-100 text-cyan-700';
    case 'diocesis': return 'bg-indigo-100 text-indigo-700';
    case 'transferencia_cuota_iglesia': return 'bg-amber-100 text-amber-700';
    case 'financiamiento': return 'bg-teal-100 text-teal-700';
    default: return 'bg-slate-100 text-slate-600';
  }
}

function getOrigenLabel(mov: MovOrigen): string {
  if (mov.categoria_aporte === 'cuota_iglesia') {
    return esCuotaPA(mov) ? 'Cuota iglesia · PA' : 'Cuota iglesia · JAL';
  }
  switch (mov.origen_tipo) {
    case 'miembro': return 'Miembro';
    case 'externo': return 'Externo';
    case 'lumisial': return 'Lumisial';
    case 'diocesis': return 'Diócesis';
    case 'transferencia_cuota_iglesia': return 'Cuota iglesia';
    case 'financiamiento': return 'Financiamiento';
    default: {
      if (mov.categoria_egreso) return CATEGORIA_EGRESO_LABELS[mov.categoria_egreso] ?? mov.categoria_egreso;
      if (mov.categoria_aporte) return 'Aporte mensual';
      return 'N/A';
    }
  }
}

const EMPTY_RESPONSE: PaginatedResponse = { data: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };

interface ParaAdjuntar {
  readonly items: readonly ItemParaAdjuntar[];
  readonly modo: 'adjuntar' | 'reemplazar';
}

/** La entidad dueña del dinero; la vista global no tiene una sola, así que no gestiona comprobantes. */
function entidadDeDinero(ownerContext: OwnerContext): EntidadDinero | null {
  return ownerContext.tipo === 'global' ? null : { ...ownerContext, tipo: ownerContext.tipo };
}

interface VentanaAdjuntarProps {
  readonly paraAdjuntar: ParaAdjuntar | null;
  readonly entidad: EntidadDinero | null;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

const VentanaAdjuntar: React.FC<VentanaAdjuntarProps> = ({ paraAdjuntar, entidad, onClose, onSuccess }) => {
  if (!paraAdjuntar || !entidad) return null;
  return (
    <AdjuntarComprobanteModal
      isOpen
      entidad={entidad}
      items={paraAdjuntar.items}
      modo={paraAdjuntar.modo}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

export const MovimientosJAView: React.FC<MovimientosJAViewProps> = ({ ownerContext }) => {
  const { usuario } = useAuth();
  const [showCampanaModal, setShowCampanaModal] = useState(false);
  const puedeOperarFondo = ownerContext.tipo !== 'global' && puedeOperarDinero(usuario, {
    ...ownerContext, tipo: ownerContext.tipo, diocesisId: null, provinciaEclesiasticaId: null,
  });
  const [response, setResponse] = useState<PaginatedResponse>(EMPTY_RESPONSE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showGastoModal, setShowGastoModal] = useState(false);
  const [showIngresoModal, setShowIngresoModal] = useState(false);
  const { recargar: recargarRegularizacion } = useRegularizacion();
  const [ahora, setAhora] = useState(() => new Date());
  const [paraAdjuntar, setParaAdjuntar] = useState<ParaAdjuntar | null>(null);
  const [paraReembolsar, setParaReembolsar] = useState<Movimiento | null>(null);
  const entidad = entidadDeDinero(ownerContext);

  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState<'ingreso' | 'egreso' | ''>('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [mostrarAnulados, setMostrarAnulados] = useState(false);

  const buildFilters = useCallback((): MovimientoFilters => {
    const filters: MovimientoFilters = {};
    if (tipo) filters.tipo = tipo;
    if (fechaInicio) filters.fechaInicio = fechaInicio;
    if (fechaFin) filters.fechaFin = fechaFin;
    if (busqueda.trim()) filters.busqueda = busqueda.trim();
    if (mostrarAnulados) filters.incluirAnulados = true;
    return filters;
  }, [tipo, fechaInicio, fechaFin, busqueda, mostrarAnulados]);

  const loadMovimientos = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      const data = await movimientoService.getPaginatedMovimientosJA(ownerContext, buildFilters(), page);
      setResponse(data);
      setAhora(new Date());
    } catch (err) {
      setError('No pudimos cargar los movimientos.');
      reportarErrorInesperado(err, 'No pudimos cargar los movimientos.');
    } finally {
      setLoading(false);
    }
  }, [ownerContext, buildFilters]);

  useEffect(() => {
    loadMovimientos(1);
  }, [loadMovimientos]);

  const handleFilterChange = () => { loadMovimientos(1); };

  const abrirAdjuntar = (mov: Movimiento, respaldo: Respaldo, modo: 'adjuntar' | 'reemplazar') => {
    setParaAdjuntar({
      modo,
      items: [{
        movimientoId: mov.id,
        respaldo,
        descripcion: mov.descripcion ?? null,
        monto: Number(mov.monto),
        fecha: respaldo === 'reembolso' ? mov.reembolso_fecha ?? mov.fecha : mov.fecha,
      }],
    });
  };
  const alAdjuntarDesdeFila = puedeOperarFondo ? abrirAdjuntar : undefined;

  const resetFilters = () => {
    setBusqueda('');
    setTipo('');
    setFechaInicio('');
    setFechaFin('');
    setTimeout(() => loadMovimientos(1), 0);
  };

  const hasActiveFilters = !!(busqueda || tipo || fechaInicio || fechaFin);

  const renderMovimientos = () => {
    if (loading) {
      return (
        <div className="space-y-3 py-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      );
    }
    // Con la carga fallida la lista está vacía porque no se pudo leer, no porque no haya nada:
    // el aviso de error ya lo explica arriba y decir "no hay movimientos" sería mentir.
    if (error) return null;
    if (response.data.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">
          <p className="mb-2">No hay movimientos registrados. Registra un ingreso o egreso.</p>
          {hasActiveFilters && <p className="text-sm">Intenta cambiar los filtros</p>}
        </div>
      );
    }
    return (
      <>
        {/* Vista tabla — solo en sm+ */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Fecha</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Tipo</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Monto</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Descripción</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Categoría</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Comprobante</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {response.data.map((mov) => {
                const montoBase = mov.tipo === 'ingreso' ? CLS_INGRESO : CLS_EGRESO;
                const montoColorClass = mov.anulado ? `${CLS_ANULADO} line-through` : montoBase;
                const tipoColor = mov.anulado ? CLS_ANULADO : montoBase;
                const tipoColorThrough = mov.anulado ? `${CLS_ANULADO} line-through` : montoBase;
                return (
                  <tr
                    key={mov.id}
                    className={`transition-colors ${mov.anulado ? 'bg-slate-100 opacity-60' : 'hover:bg-slate-50'}`}
                  >
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {formatDate(mov.fecha)}
                        {mov.anulado && (
                          <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-700 rounded-full font-medium">
                            Anulado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {mov.tipo === 'ingreso' ? (
                          <>
                            <TrendingUp className={`w-5 h-5 ${tipoColor}`} />
                            <span className={`text-sm font-medium ${tipoColorThrough}`}>Ingreso</span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className={`w-5 h-5 ${tipoColor}`} />
                            <span className={`text-sm font-medium ${tipoColorThrough}`}>Egreso</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-bold ${montoColorClass}`}>
                        {mov.tipo === 'ingreso' ? '+' : '-'} {formatCurrency(Number(mov.monto))}
                      </span>
                      {mov.monto_original != null && <MontoCorregido montoOriginal={Number(mov.monto_original)} />}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                      <span className={mov.anulado ? 'line-through' : ''}>{mov.descripcion || '-'}</span>
                      {mov.anulado && mov.motivo_anulacion && (
                        <p className="text-xs text-slate-400 mt-1 italic">{mov.motivo_anulacion}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2.5 py-1.5 rounded-full font-medium ${getOrigenBadgeClass(mov)}`}>
                        {getOrigenLabel(mov)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <ComprobantesDeMovimiento movimiento={mov} ahora={ahora} onAdjuntar={alAdjuntarDesdeFila} onReembolsar={puedeOperarFondo ? setParaReembolsar : undefined} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Vista cards — solo en mobile */}
        <div className="sm:hidden space-y-3">
          {response.data.map((mov) => {
            const montoBase = mov.tipo === 'ingreso' ? CLS_INGRESO : CLS_EGRESO;
            const montoColorClass = mov.anulado ? `${CLS_ANULADO} line-through` : montoBase;
            const iconColor = mov.anulado ? CLS_ANULADO : montoBase;
            return (
              <div
                key={mov.id}
                className={`rounded-xl border p-4 ${mov.anulado ? 'bg-slate-100 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {mov.tipo === 'ingreso'
                      ? <TrendingUp className={`w-4 h-4 ${iconColor}`} />
                      : <TrendingDown className={`w-4 h-4 ${iconColor}`} />}
                    <span className="text-sm text-slate-500">{formatDate(mov.fecha)}</span>
                    {mov.anulado && (
                      <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-700 rounded-full font-medium">Anulado</span>
                    )}
                  </div>
                  <span className={`text-sm font-bold ${montoColorClass}`}>
                    {mov.tipo === 'ingreso' ? '+' : '-'} {formatCurrency(Number(mov.monto))}
                    {mov.monto_original != null && <MontoCorregido montoOriginal={Number(mov.monto_original)} />}
                  </span>
                </div>
                <p className={`text-sm text-slate-700 mb-2 ${mov.anulado ? 'line-through' : ''}`}>
                  {mov.descripcion || '-'}
                </p>
                {mov.anulado && mov.motivo_anulacion && (
                  <p className="text-xs text-slate-400 italic mb-2">{mov.motivo_anulacion}</p>
                )}
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getOrigenBadgeClass(mov)}`}>
                  {getOrigenLabel(mov)}
                </span>
                <div className="mt-2">
                  <ComprobantesDeMovimiento movimiento={mov} ahora={ahora} onAdjuntar={alAdjuntarDesdeFila} onReembolsar={puedeOperarFondo ? setParaReembolsar : undefined} />
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Movimientos</h2>
          <p className="text-slate-600 text-sm mt-1">Registro de ingresos y egresos</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowIngresoModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Registrar Ingreso
          </button>
          <button
            type="button"
            onClick={() => setShowGastoModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Registrar Gasto
          </button>
        </div>
      </div>

      <FormErrorAlert
        error={error}
        onClear={() => { setError(null); }}
        action={{ label: 'Reintentar', onClick: () => { void loadMovimientos(1); } }}
      />

      {entidad && puedeOperarFondo && (
        <ComprobantesPendientesPanel entidad={entidad} onCambio={() => { loadMovimientos(response.page); }} />
      )}

      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyUp={handleFilterChange}
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors font-semibold ${showFilters || hasActiveFilters ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <Filter className="w-5 h-5" />
            Filtros
            {hasActiveFilters && <span className="ml-2 bg-white/30 px-2 py-1 rounded text-xs">Activos</span>}
          </button>
        </div>

        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-6 mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Select
                  label="Tipo"
                  options={[
                    { value: 'ingreso', label: 'Ingresos' },
                    { value: 'egreso', label: 'Egresos' },
                  ]}
                  value={tipo}
                  onChange={(v) => { setTipo(v as 'ingreso' | 'egreso' | ''); handleFilterChange(); }}
                  placeholder="Todos"
                />
              </div>
              <div>
                <label htmlFor="filtro-fecha-inicio" className="block text-sm font-semibold text-slate-700 mb-2">Fecha Inicio</label>
                <input
                  id="filtro-fecha-inicio"
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); handleFilterChange(); }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>
              <div>
                <label htmlFor="filtro-fecha-fin" className="block text-sm font-semibold text-slate-700 mb-2">Fecha Fin</label>
                <input
                  id="filtro-fecha-fin"
                  type="date"
                  value={fechaFin}
                  onChange={(e) => { setFechaFin(e.target.value); handleFilterChange(); }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm"
                >
                  <X className="w-4 h-4" />
                  Limpiar filtros
                </button>
              )}
              <label className="flex items-center gap-2 ml-auto cursor-pointer text-sm text-slate-500 hover:text-slate-700">
                <input
                  type="checkbox"
                  checked={mostrarAnulados}
                  onChange={(e) => { setMostrarAnulados(e.target.checked); setTimeout(() => loadMovimientos(1), 0); }}
                  className="w-4 h-4 text-slate-600 border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
                <span>Mostrar anulados</span>
              </label>
            </div>
          </div>
        )}

        {renderMovimientos()}

        {response.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Página {response.page} de {response.totalPages} ({response.total} movimientos)
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => loadMovimientos(response.page - 1)}
                disabled={response.page === 1}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                Anterior
              </button>
              <button
                type="button"
                onClick={() => loadMovimientos(response.page + 1)}
                disabled={response.page === response.totalPages}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {puedeOperarFondo && <>
        <button type="button" onClick={() => setShowCampanaModal(true)} className="rounded-lg border px-4 py-2">Movimiento de fondo de campaña</button>
        <MovimientoCampanaModal isOpen={showCampanaModal} onClose={() => setShowCampanaModal(false)}
          onSuccess={() => loadMovimientos(1)} ownerContext={{ ...ownerContext, tipo: ownerContext.tipo }} />
      </>}
      <GastoModal
        isOpen={showGastoModal}
        onClose={() => setShowGastoModal(false)}
        onSuccess={() => loadMovimientos(1)}
        ownerContext={ownerContext}
      />
      <IngresoJAModal
        isOpen={showIngresoModal}
        onClose={() => setShowIngresoModal(false)}
        onSuccess={() => loadMovimientos(1)}
        ownerContext={ownerContext}
      />
      <VentanaAdjuntar
        paraAdjuntar={paraAdjuntar}
        entidad={entidad}
        onClose={() => { setParaAdjuntar(null); }}
        onSuccess={() => { loadMovimientos(response.page); recargarRegularizacion(); }}
      />
      {paraReembolsar && entidad && puedeOperarFondo && (
        <ReembolsoModal key={paraReembolsar.id} isOpen movimiento={paraReembolsar} entidad={entidad}
          onClose={() => setParaReembolsar(null)}
          onSuccess={() => { void loadMovimientos(response.page); recargarRegularizacion(); }} />
      )}
    </div>
  );
};
